import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { Crop } from 'react-image-crop';

import { Alert, AlertSize, AlertType } from '../../components/Alert/Alert';
import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { ModalDecorator } from '../../components/ModalDecorator/ModalDecorator';
import { Bem } from '../../service/Bem/Bem';
import { Size, ValidationErrorData } from '../../typings/common';
import { CreativeImageCrop } from '../CreativeImageCrop/CreativeImageCrop';
import { CreativeSizesList } from '../CreativeSizesList/CreativeSizesList';
import { CreativeSize } from '../CreativeSizesList/typings';

import './CreativeImageCropModal.css';
import * as keyset from './CreativeImageCropModal.i18n';

const i18n = i18nFactory(keyset);

export interface CreativeImageCropModalProps {
    isVisible: boolean;
    isLoading?: boolean;
    error?: ValidationErrorData;
    onClose: () => void;
    onSave: (image: HTMLImageElement) => void;

    isEditMode?: boolean;
    src?: string;
    crop?: Crop;
    minWidth?: number;
    minHeight?: number;
    onCropComplete: (crop: Crop) => void;
    onImageLoaded: (naturalImageSize: Size) => void;

    sizes?: CreativeSize[];
    selectedSize?: number;
    onChangeSize: (value: number) => void;
}

const BLOCK = 'creative-image-crop-modal';

export class CreativeImageCropModal extends React.PureComponent<CreativeImageCropModalProps> {
    static BottomControls = React.memo(
        ({ isEditMode, onSave, onClose }: { isEditMode?: boolean; onClose: () => void; onSave: () => void }) => (
            <>
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'add-button')}
                    size="n"
                    text={isEditMode ? i18n('crop-updated-sizes') : i18n('crop-all-sizes')}
                    theme="action"
                    view="classic"
                    onClick={onSave}
                />
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'cancel-button')}
                    size="n"
                    text={i18n('cancel')}
                    theme="normal"
                    view="classic"
                    onClick={onClose}
                />
            </>
        )
    );

    private image: HTMLImageElement;

    render(): React.ReactNode {
        const {
            isVisible,
            isLoading,
            src,
            crop,
            onCropComplete,
            sizes,
            minWidth,
            minHeight,
            selectedSize,
            onChangeSize
        } = this.props;

        const loading = !crop || isLoading;

        return (
            <ModalDecorator
                header={i18n('crop-title')}
                footer={this.renderFooter()}
                visible={isVisible}
                progress={loading}
                cls={BLOCK}
                theme="full-width-body-with-borders"
            >
                <div
                    className={Bem.cls(BLOCK, 'content-wrapper', {
                        loading
                    })}
                >
                    {sizes && selectedSize !== undefined && (
                        <div className={Bem.cls(BLOCK, 'sizes')}>
                            <CreativeSizesList sizes={sizes} selectedSize={selectedSize} onChangeSize={onChangeSize} />
                        </div>
                    )}
                    {src && (
                        <CreativeImageCrop
                            src={src}
                            crop={crop}
                            minWidth={minWidth}
                            minHeight={minHeight}
                            onCropComplete={onCropComplete}
                            onImageLoaded={this.onImageLoaded}
                        />
                    )}
                </div>
            </ModalDecorator>
        );
    }

    private renderFooter = (): React.ReactNode => {
        const { isEditMode, onClose } = this.props;

        return (
            <>
                {this.renderError()}
                <CreativeImageCropModal.BottomControls onSave={this.onSave} onClose={onClose} isEditMode={isEditMode} />
            </>
        );
    };

    private renderError = (): React.ReactNode => {
        const { error } = this.props;

        if (!error) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'error')}>
                <Alert type={AlertType.error} size={AlertSize.s}>
                    {error.text}
                </Alert>
            </div>
        );
    };

    private onSave = (): void => {
        this.props.onSave(this.image);
    };

    private onImageLoaded = (naturalImageSize: Size, imageSize: Size, image: HTMLImageElement): void => {
        this.image = image;

        this.props.onImageLoaded(naturalImageSize);
    };
}
